import { useState, useCallback } from 'react';

interface DialogState {
  isOpen: boolean;
  title: string;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void;
  showCancel?: boolean;
}

export function useDialog() {
  const [dialog, setDialog] = useState<DialogState>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
  });

  const showDialog = useCallback((options: Omit<DialogState, 'isOpen'>) => {
    setDialog({ ...options, isOpen: true });
  }, []);

  const closeDialog = useCallback(() => {
    setDialog((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const showSuccess = useCallback((title: string, message: string, onConfirm?: () => void) => {
    showDialog({ title, message, type: 'success', onConfirm });
  }, [showDialog]);

  const showError = useCallback((title: string, message: string) => {
    showDialog({ title, message, type: 'error' });
  }, [showDialog]);

  const showWarning = useCallback((title: string, message: string, onConfirm?: () => void) => {
    showDialog({ title, message, type: 'warning', onConfirm });
  }, [showDialog]);

  const showInfo = useCallback((title: string, message: string) => {
    showDialog({ title, message, type: 'info' });
  }, [showDialog]);

  const showConfirm = useCallback(
    (title: string, message: string, onConfirm: () => void, confirmText = 'Confirm', cancelText = 'Cancel') => {
      showDialog({
        title,
        message,
        type: 'warning',
        showCancel: true,
        confirmText,
        cancelText,
        onConfirm,
      });
    },
    [showDialog]
  );

  return {
    dialog,
    closeDialog,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showConfirm,
  };
}
